import { useState, useCallback, useMemo } from "react";
import { getTimezoneOptions, getUserTimezone } from "@core/features/timezones";
import { useProfileMutation, normalizeUserData } from "@core/features/profile";
import type { RawUserData } from "@core/features/profile";
import { usePlatformStore } from "@platform/store";
import type { User } from "@platform/types/platform.types";
import type { TimezoneOption } from "@themes/pollinator/ui/TimezoneSelect";
import { TimezoneSubStep } from "./TimezoneSubStep";

export type Props = {
  /** Program ID */
  programId: number;
  /** Handler for next button (called after successful save) */
  onNext: () => void;
  /** Handler for back button (hidden if undefined) */
  onBack?: () => void;
  /** Handler for skip button (hidden if undefined) */
  onSkip?: () => void;
  /** Current step index (0-indexed) */
  stepIndex: number;
  /** Total steps */
  totalSteps: number;
};

/**
 * Container for TimezoneSubStep that handles data fetching and mutations.
 */
export function TimezoneSubStepContainer({
  programId: _programId,
  onNext,
  onBack,
  onSkip,
  stepIndex,
  totalSteps,
}: Props) {
  const user = usePlatformStore((s) => s.user);
  const setUser = usePlatformStore((s) => s.setUser);
  const timezoneOptions = getTimezoneOptions();

  // Get user's current timezone or detect from browser
  const initialTimezone = useMemo(() => {
    if (user?.timezone) {
      return (
        timezoneOptions.find((opt) => opt.value === user.timezone) ?? null
      );
    }
    // Try to detect from browser
    return getUserTimezone(timezoneOptions);
  }, [user?.timezone, timezoneOptions]);

  const [selectedTimezone, setSelectedTimezone] =
    useState<TimezoneOption | null>(initialTimezone);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizedUser = user
    ? normalizeUserData(user as unknown as RawUserData)
    : null;

  const { mutateAsync } = useProfileMutation({
    userRole: user?.role || "",
    previousValues: normalizedUser,
    onUserUpdate: (userData) => {
      setUser(userData as User);
    },
  });

  const handleContinue = useCallback(async () => {
    if (!selectedTimezone || !normalizedUser) return;

    setIsSubmitting(true);
    try {
      await mutateAsync({
        ...normalizedUser,
        timezone: [selectedTimezone.value],
      });
      onNext();
    } catch (error) {
      console.error("Failed to save timezone:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedTimezone, normalizedUser, mutateAsync, onNext]);

  const handleTimezoneChange = useCallback(
    (timezone: TimezoneOption | null) => {
      setSelectedTimezone(timezone);
    },
    []
  );

  return (
    <TimezoneSubStep
      timezoneOptions={timezoneOptions}
      selectedTimezone={selectedTimezone}
      onTimezoneChange={handleTimezoneChange}
      onContinue={handleContinue}
      onBack={onBack}
      onSkip={onSkip}
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      isPrimaryDisabled={isSubmitting || !selectedTimezone}
    />
  );
}

export default TimezoneSubStepContainer;
